import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { Capacitor } from '@capacitor/core';

const AuthContext = createContext(null);

const OAUTH_PENDING_KEY = 'ignite_oauth_pending';

// Returns true if the current URL looks like an OAuth callback
function isOAuthCallback() {
  const p = new URLSearchParams(window.location.search);
  return p.has('code') || p.has('error') || sessionStorage.getItem(OAUTH_PENDING_KEY) === '1';
}

export function AuthProvider({ children }) {
  const [user, setUser]                   = useState(null);
  const [dbUser, setDbUser]               = useState(null);
  const [loading, setLoading]             = useState(true);
  const [needsUsername, setNeedsUsername] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // ── Masquerade state — persisted in sessionStorage so it survives page refresh
  const [masquerade, setMasquerade] = useState(() => {
    const id = sessionStorage.getItem('masq_user_id');
    if (!id) return null;
    return {
      targetId:   id,
      targetName: sessionStorage.getItem('masq_user_name') || 'User',
      adminId:    sessionStorage.getItem('masq_admin_id')  || null,
      adminName:  sessionStorage.getItem('masq_admin_name') || 'Admin',
    };
  });

  const fetchDbUser = useCallback(async () => {
    try {
      const { api } = await import('../api/client');
      const d = await api.get('/api/profile');
      const u = d.user || null;
      setDbUser(u);
      setNeedsUsername(!!u && !u.username);
      setNeedsOnboarding(!!u && !u.hasOnboarded);
      return u;
    } catch {
      setDbUser(null);
      return null;
    }
  }, []);

  // If the user landed via an invite link before signing up, claim it now that
  // they're authenticated. Idempotent (server-side) and never blocks login.
  const claimPendingInvite = useCallback(async () => {
    let token;
    try { token = localStorage.getItem('pendingInviteToken'); } catch {}
    if (!token) return;
    try { localStorage.removeItem('pendingInviteToken'); } catch {}
    try {
      const { invitesApi } = await import('../api/client');
      await invitesApi.claim(token);
    } catch {}
  }, []);

  const hydrateUser = useCallback(async () => {
    try {
      const u = await Auth.currentAuthenticatedUser();
      setUser(u);
      await fetchDbUser();
      await claimPendingInvite();
      return true;
    } catch {
      return false;
    }
  }, [fetchDbUser, claimPendingInvite]);

  useEffect(() => {
    let cancelled = false;

    // Hub listener — the primary path for OAuth callbacks.
    // Register this FIRST, before any async work, so we never miss the event.
    const unsubscribe = Hub.listen('auth', async ({ payload: { event } }) => {
      if (event === 'signIn' || event === 'cognitoHostedUI') {
        sessionStorage.removeItem(OAUTH_PENDING_KEY);
        if (cancelled) return;
        try {
          const u = await Auth.currentAuthenticatedUser();
          if (cancelled) return;
          setUser(u);
          await fetchDbUser();
          await claimPendingInvite();
        } catch {}
        if (!cancelled) setLoading(false);
        cancelled = true; // prevent init() from also calling setLoading
      } else if (event === 'signOut') {
        setUser(null); setDbUser(null); setNeedsUsername(false);
      } else if (event === 'signIn_failure' || event === 'cognitoHostedUI_failure') {
        sessionStorage.removeItem(OAUTH_PENDING_KEY);
        if (!cancelled) setLoading(false);
        cancelled = true;
      }
    });

    // Deep-link handler: when iOS/Android opens the app via com.theigniteapp.app://
    // after Google/Apple OAuth, reload the WebView with the ?code= params so
    // Amplify can exchange the authorization code for tokens.
    let appUrlListener = null;
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App: CapApp }) => {
        CapApp.addListener('appUrlOpen', async ({ url }) => {
          const queryStart = url.indexOf('?');
          if (queryStart !== -1 && (url.includes('code=') || url.includes('error='))) {
            // Dismiss SFSafariViewController before the WebView navigates away (iOS only).
            if (Capacitor.isNativePlatform()) {
              try {
                const { Browser } = await import('@capacitor/browser');
                await Browser.close();
              } catch {}
            }
            // Use the fixed app server URL, not window.location.origin — after the OAuth
            // redirect the WebView may still be on the Cognito domain, making origin wrong.
            window.location.replace('https://theigniteapp.com' + url.slice(queryStart));
          }
        }).then(listener => { appUrlListener = listener; });
      });
    }

    async function init() {
      if (isOAuthCallback()) {
        // We're on an OAuth callback page — Amplify is exchanging the code.
        // Poll until tokens land in storage (Hub is also watching in parallel).
        // Each attempt: wait 400ms, try currentAuthenticatedUser.
        // Max wait: 40 × 400ms = 16 seconds.
        for (let i = 0; i < 40; i++) {
          await new Promise(r => setTimeout(r, 400));
          if (cancelled) return; // Hub already handled it
          const ok = await hydrateUser();
          if (ok) {
            sessionStorage.removeItem(OAUTH_PENDING_KEY);
            if (!cancelled) setLoading(false);
            cancelled = true;
            return;
          }
        }
        // Timed out — clear flag and let user try again
        sessionStorage.removeItem(OAUTH_PENDING_KEY);
        if (!cancelled) setLoading(false);
      } else {
        // Normal page load — check for existing session immediately
        await hydrateUser();
        if (!cancelled) setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
      unsubscribe();
      appUrlListener?.remove();
    };
  }, [hydrateUser, fetchDbUser, claimPendingInvite]);

  async function signIn(email, password) {
    const u = await Auth.signIn(email, password);
    if (u.challengeName === 'NEW_PASSWORD_REQUIRED') {
      return u; // caller must call completeNewPassword to finish auth
    }
    setUser(u);
    setTimeout(fetchDbUser, 300);
    return u;
  }

  async function completeNewPassword(challengeUser, newPassword) {
    const u = await Auth.completeNewPasswordChallenge(challengeUser, newPassword, {});
    setUser(u);
    setTimeout(fetchDbUser, 300);
    return u;
  }

  async function signUp(name, email, password) {
    return Auth.signUp({ username: email, password, attributes: { email, name } });
  }

  async function confirmSignUp(email, code) { return Auth.confirmSignUp(email, code); }
  async function resendCode(email)          { return Auth.resendSignUp(email); }
  async function forgotPassword(email)      { return Auth.forgotPassword(email); }
  async function resetPassword(email, code, password) {
    return Auth.forgotPasswordSubmit(email, code, password);
  }

  async function federatedSignIn(provider) {
    sessionStorage.setItem(OAUTH_PENDING_KEY, '1');
    await Auth.federatedSignIn({ provider });
  }

  async function signOut() {
    // Clear masquerade on sign out
    ['masq_user_id','masq_user_name','masq_admin_id','masq_admin_name'].forEach(k => sessionStorage.removeItem(k));
    setMasquerade(null);
    await Auth.signOut();
    setUser(null); setDbUser(null); setNeedsUsername(false);
  }

  function completeUsernameSetup() {
    setNeedsUsername(false);
    fetchDbUser();
  }

  function startMasquerade(targetUser, currentDbUser) {
    sessionStorage.setItem('masq_user_id',    targetUser.id);
    sessionStorage.setItem('masq_user_name',  targetUser.name);
    sessionStorage.setItem('masq_admin_id',   currentDbUser.id);
    sessionStorage.setItem('masq_admin_name', currentDbUser.name);
    setMasquerade({
      targetId:   targetUser.id,
      targetName: targetUser.name,
      adminId:    currentDbUser.id,
      adminName:  currentDbUser.name,
    });
    // Re-fetch /api/profile — now returns target user's profile via masquerade header
    fetchDbUser();
  }

  function stopMasquerade() {
    ['masq_user_id','masq_user_name','masq_admin_id','masq_admin_name'].forEach(k => sessionStorage.removeItem(k));
    setMasquerade(null);
    // Re-fetch /api/profile — now returns admin's profile (no masquerade header)
    fetchDbUser();
  }

  const value = {
    user, dbUser, loading, needsUsername, needsOnboarding,
    signIn, signUp, confirmSignUp, resendCode, signOut,
    forgotPassword, resetPassword, federatedSignIn, completeNewPassword,
    completeUsernameSetup,
    refreshDbUser: fetchDbUser,
    clearOnboarding: () => setNeedsOnboarding(false),
    isAuthenticated: !!user,
    isAdmin:   dbUser?.role === 'admin',
    userName:  user?.attributes?.name || user?.username || '',
    userEmail: user?.attributes?.email || '',
    masquerade,
    startMasquerade,
    stopMasquerade,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
